/**
 * Сетевой слой трека — единственное место, откуда клиент обращается к серверу.
 *
 * Слой, расползшийся по экранам, нельзя ни подменить в тесте целиком, ни
 * прочитать глазами за один заход, а шаг ручного чек-листа занят именно этим.
 * Правило то же, что решение `D27` дало треку Android, и живёт оно здесь.
 *
 * Базовый адрес приходит переменной сборщика, и **умолчания у неё нет**
 * (решение `D36`): клиент, молча ушедший на адрес самой страницы, выглядит
 * работающим ровно до выкладки. Адреса литералом в треке нет ни одного.
 *
 * Токен живёт в памяти вкладки (решение `D28`): ни хранилища, ни cookie в
 * треке нет, перезагрузка страницы — повторный вход.
 *
 * Бизнес-правил здесь нет и быть не может: ни таблицы «роль → что можно», ни
 * перечня разрешённых переходов, ни отбора ленты. Отказ опознаётся машинным
 * кодом конверта — не текстом сообщения, который переводится на язык запроса,
 * и не транспортным статусом в одиночку.
 *
 * ПЕРЕВОРОТ блока `WEB-1.1` (спецификация §7): подписи входа принимают **канал
 * из двух** и согласие с условиями. Форма тела задана контрактом: «Канал ровно
 * один из двух. Какой именно — выбирает клиент; проверку „хотя бы один канал
 * указан“ выполняет сервер». Поэтому канал выражен объединением, а не парой
 * необязательных полей: пара позволила бы отправить оба ключа сразу или ни
 * одного, и поймал бы это только сервер.
 *
 * Проверок формата телефона и почты здесь нет ни одной, и это не упущение:
 * клиент решает, **в какое поле тела** положить введённое, а годность значения
 * решает сервер. Своя проверка разошлась бы с серверной молча.
 */

import { errorCode, errorDetails, errorMessage, isErrorEnvelope, unwrap } from './contract';
import type {
  BodyTypeInfo,
  CargoBodyInput,
  CargoFeedFilters,
  CargoListing,
  CargoListingInput,
  CargoOffer,
  CargoOfferInput,
  CargoSlot,
  City,
  Company,
  CompanyCreateRequest,
  CompanyDriver,
  CompanyExpense,
  CompanyMember,
  CompanyPayout,
  CompanyPayoutInput,
  CurrentUserData,
  DataEnvelope,
  Document,
  DocumentLink,
  ErrorCode,
  ErrorDetail,
  FleetVehicle,
  Invitation,
  ListingMapCityList,
  MemberPermissions,
  MembershipRole,
  PageEnvelope,
  Permission,
  PublicTracking,
  RepairStateChangeRequest,
  ServiceRecord,
  ServiceRecordCreateRequest,
  Session,
  User,
  UserUpdateRequest,
  Vehicle,
  VehicleCard,
  VehicleCreateRequest,
  VehicleFault,
  VehiclePhoto,
  VinClaim,
  VinClaimCreateRequest,
  Work,
  WorkAssignmentInput,
} from './contract';

/** Имя переменной сборщика. Называется в треке ровно один раз — здесь. */
export const API_BASE_URL_VARIABLE = 'VITE_API_BASE_URL';

/** Машинный код, по которому вкладка возвращается на вход. */
export const UNAUTHENTICATED_CODE: ErrorCode = 'UNAUTHENTICATED';

/**
 * Базовый адрес сервера из окружения сборщика.
 *
 * Пустое и пробельное значение — то же, что отсутствие: переменная, заданная
 * пустой строкой, отличается от незаданной только в отчёте сборщика.
 */
export function readApiBaseUrl(env: Readonly<Record<string, unknown>>): string {
  const declared = env[API_BASE_URL_VARIABLE];

  if (typeof declared !== 'string' || declared.trim() === '') {
    throw new Error(
      `Не задан адрес сервера: переменная ${API_BASE_URL_VARIABLE} пуста или отсутствует. `
      + 'Умолчания у неё нет намеренно — задайте её и запустите приложение снова.',
    );
  }

  return declared.trim();
}

/**
 * Отказ обращения к API.
 *
 * Машинный код есть только у отказа, пришедшего конвертом контракта. Обрыв
 * связи, стоковая страница фреймворка и посредник, ответивший по-своему, дают
 * тот же отказ без кода: выдумывать за них код значит решать по транспорту.
 */
export class ApiFailure extends Error {
  readonly code: ErrorCode | null;
  readonly requestId: string | null;
  /**
   * Сообщения сервера по полям формы — форма контракта, а не ветвление.
   *
   * Экран кладёт `detail.message` рядом с элементом, чьё имя равно
   * `detail.field`, и ни одного имени поля при этом не знает. Разбор `details`
   * ради решения «что делать дальше» был бы переносом правила в клиент.
   */
  readonly details: readonly ErrorDetail[];
  /**
   * Текст отказа, написанный сервером, — для показа, а не для решения.
   *
   * Экран показывает его человеку, когда отказ осмысленный: правило «нельзя
   * обезоружить единственного директора» знает сервер, и своими словами его
   * пересказал бы клиент, заведя правило (долг `TD-67`). Ветвления по этому
   * тексту нет ни одного — решает машинный код.
   *
   * `null` означает «сервер не сказал ничего»: отказ пришёл без конверта
   * контракта — обрыв связи, стоковая страница фреймворка, посредник.
   *
   * Имя не `message` намеренно: `message` у отказа уже есть — его объявляет
   * `Error`, и там стоит строка самого клиента. Два разных текста под одним
   * именем спорили бы молча.
   */
  readonly reason: string | null;

  constructor(
    code: ErrorCode | null,
    requestId: string | null,
    details: readonly ErrorDetail[] = [],
    reason: string | null = null,
  ) {
    super('Обращение к API отклонено');

    this.name = 'ApiFailure';
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.reason = reason;
  }
}

/** Что отдаёт подтверждение кода: токен и профиль, пришедшие одним ответом. */
export interface AuthSession {
  readonly token: string;
  readonly user: User;
}

export interface ApiClientOptions {
  readonly baseUrl: string;
}

/** Канал одноразового кода. Ровно один из двух — выбирает клиент. */
export type AuthChannel = 'phone' | 'email';

/**
 * Тело запроса кода: **один** ключ канала и второго нет вовсе.
 *
 * Объединение, а не два необязательных поля: форма, допускающая оба ключа
 * сразу, отличается от контракта ровно тем, что расхождение с ним ловит только
 * сервер.
 */
export type AuthCodeInput = { readonly phone: string } | { readonly email: string };

/**
 * Тело подтверждения: тот же единственный ключ канала, код и согласие.
 *
 * Согласие объявлено значением `true`, а не `boolean`: «не отмечено» — это
 * **отсутствие ключа**, а не ложь. Кто новый пользователь, а кто прежний,
 * решает сервер, и отправленное «нет» было бы утверждением клиента о том,
 * чего он не знает.
 */
export type AuthVerifyInput =
  | { readonly phone: string; readonly code: string; readonly terms_accepted?: true }
  | { readonly email: string; readonly code: string; readonly terms_accepted?: true };

/**
 * Тело подтверждения второго канала: тот же единственный ключ канала и код.
 *
 * Согласия здесь нет: оно принадлежит входу, создающему аккаунт, а второй
 * канал добавляет уже вошедший. Форма тела — та же, что у входа, потому что
 * так её объявляет контракт.
 */
export type ChannelConfirmInput =
  | { readonly phone: string; readonly code: string }
  | { readonly email: string; readonly code: string };

/**
 * Тело приглашения: роль и **один** ключ канала.
 *
 * Объединение, а не два необязательных поля, — по той же причине, что и у
 * входа: форма, допускающая оба ключа сразу, расходится с контрактом так, что
 * поймает это только сервер. Сгенерированный тип тела здесь не годится:
 * `anyOf` контракта вырождается в `unknown`.
 */
export type InvitationInput =
  | { readonly phone: string; readonly role: MembershipRole }
  | { readonly email: string; readonly role: MembershipRole };

/**
 * Тело формы объявления о грузе — поля `CargoListingInput` и ни одного сверх.
 *
 * От схемы оно отличается ровно одним: незаполненное поле уходит `null`, а не
 * выдуманным значением. Обязательность, пределы и даты судит сервер (`422` по
 * полю), и ноль вместо пустого веса был бы утверждением клиента о том, чего
 * человек не вводил.
 */
export type CargoBodySubmission = { readonly [K in keyof CargoBodyInput]: CargoBodyInput[K] | null };
export type CargoListingSubmission = {
  readonly [K in Exclude<keyof CargoListingInput, 'body'>]: CargoListingInput[K] | null;
} & { readonly body: CargoBodySubmission };

/**
 * Часть тела `multipart` — блок `WEB-2.4`: имя части формы контракта и
 * значение, строка либо выбранный человеком файл. Тело из частей собирает
 * **одно** место слоя — `sendMultipart`; экран отдаёт части, а не `FormData`.
 */
export type MultipartPart = readonly [name: string, value: string | File];

/**
 * Заявка на освобождение VIN с файлом техпаспорта — встроенная форма
 * `multipart` операции `createVinClaim` (контракт `0.9.33`). Файл `null` —
 * человек ничего не выбрал: часть файла не уходит вовсе, и обязательность её
 * судит сервер (`422` под полем), а не клиент.
 */
export type VinClaimSubmission = VinClaimCreateRequest & { readonly tech_passport_file: File | null };

/**
 * Запись неисправности — встроенная форма `multipart` операции
 * `addVehicleFault`: комментарий и выбранные фото **все**, как выбраны. Число
 * фото, их тип, размер и длину комментария судит сервер.
 */
export interface VehicleFaultSubmission {
  readonly comment: string;
  readonly photos: readonly File[];
}

/**
 * Отбор ленты работ — параметры запроса `listWorks`, и только они.
 *
 * Чьи работы — решает сервер параметром `company_id` (блоки `WEB-2.2` и
 * `WEB-2.3`, контракт `0.9.34`): «работа фирмы — перевозчик или заказчик» есть
 * правило сервера, и отбор ленты клиентом завёл бы его копию.
 * `needs_assignment` — «ждёт назначения» тем же путём. Курсор непрозрачен.
 */
export interface WorksQuery {
  readonly cursor?: string;
  readonly company_id?: string;
  readonly needs_assignment?: boolean;
}

/**
 * Отбор расходов фирмы — блок `WEB-2.3`, решение формы `Б15`: работа и машина
 * уходят параметрами запроса, пересечение решает сервер. Курсор — следующей
 * страницы того же отбора.
 */
export interface ExpensesQuery {
  readonly cursor?: string;
  readonly work_id?: string;
  readonly vehicle_id?: string;
}

/** Отбор выплат фирмы — получатель и работа параметрами запроса (`Б15`). */
export interface PayoutsQuery {
  readonly cursor?: string;
  readonly membership_id?: string;
  readonly work_id?: string;
}

/**
 * Форма расхода — встроенная форма `multipart` операций `createCompanyExpense`
 * и `updateCompanyExpense` (блок `WEB-2.3`, решения формы `Б7`, `Б8`).
 *
 * Пять частей уходят всегда и строкой, как введены: пустое значение тоже
 * уходит, и обязательность, шаблон суммы и дату судит сервер (`422` под
 * полем). `null` у машины, комментария и чека — части нет вовсе: так контракт
 * говорит «нет значения», а у правки «чек не выбран» — прежний остаётся.
 */
export interface CompanyExpenseSubmission {
  readonly category: string;
  readonly amount: string;
  readonly currency: string;
  readonly spent_on: string;
  readonly work_id: string;
  readonly vehicle_id: string | null;
  readonly comment: string | null;
  readonly receipt: File | null;
}

/**
 * Тело выплаты — `CompanyPayoutInput` **целиком**: шесть ключей, и
 * незаполненное уходит `null`, а не отсутствием ключа (контракт: «необязательное
 * значение — null»). Получатель без выбора — тоже `null`: годность судит сервер.
 */
export type CompanyPayoutSubmission = { readonly [K in keyof CompanyPayoutInput]: CompanyPayoutInput[K] | null };

/**
 * Тело offer'а на груз — поля `CargoOfferInput` и ни одного сверх (блок
 * `WEB-2.2`). Незаполненное число машин уходит `null`, а не выдуманным нулём:
 * обязательность и пределы судит сервер (`422` по полю), как у формы
 * объявления. Сумма своей цены — строкой как введена; `null` — согласие на
 * условия объявления.
 */
export type CargoOfferSubmission = { readonly [K in keyof CargoOfferInput]: CargoOfferInput[K] | null };

export interface ApiClient {
  /** Токен вкладки. `null` — вход не пройден либо токен отвергнут сервером. */
  readonly token: string | null;
  /**
   * Язык гостя: уходит заголовком **только** на обращениях без токена.
   *
   * Язык владельца токена определяет профиль (контракт), и второй источник
   * спорил бы с первым.
   */
  useLocale(locale: User['locale']): void;
  requestAuthCode(input: AuthCodeInput): Promise<void>;
  verifyAuthCode(input: AuthVerifyInput): Promise<AuthSession>;
  getCurrentUser(): Promise<CurrentUserData>;
  /**
   * Правка профиля: язык и — блок `WEB-2.4` — дата рождения и пол. Тело —
   * поля `UserUpdateRequest`; прислано `null` — стереть, не прислано — не
   * трогать (контракт). Границы даты судит сервер.
   */
  updateCurrentUser(input: UserUpdateRequest): Promise<CurrentUserData>;
  createCompany(input: CompanyCreateRequest): Promise<Company>;
  listCompanyMembers(companyId: string): Promise<readonly CompanyMember[]>;
  /**
   * Страница работ. Фирма-сторона и «ждут назначения» — отбор **сервера**
   * параметрами операции (блок `WEB-2.2`, серверное требование 731); каждый
   * уходит, только когда задан, и сочетается с курсором. Подпись общая для
   * блоков `WEB-2.2`, `WEB-2.3` и `WEB-2.4` (объявлена в доске координации).
   *
   * Страница ленты работ. Блок `WEB-2.3` растит подпись параметрами отбора
   * сервера — `company_id` и `needs_assignment` (контракт `0.9.34`); без
   * доводов — прежний адрес ленты.
   */
  listWorks(options?: { cursor?: string; company_id?: string; needs_assignment?: boolean }): Promise<PageEnvelope<Work>>;

  // Операции кабинета — блок `WEB-1.2`. Перечень слоя закрыт целиком и
  // сверяется с описью операций контракта: имени, которого контракт не
  // объявлял, здесь нет, а `closeMembership` отсутствует намеренно —
  // требования на выход из фирмы нет ни одного, и заготовка под него была бы
  // заглушкой.
  /** Видимые сессии владельца токена. Признак текущей приходит полем. */
  listSessions(): Promise<readonly Session[]>;
  /** Завершение сессии, включая текущую: токен отзывается немедленно. */
  terminateSession(sessionId: string): Promise<void>;
  /** Код на второй канал. Форма тела — та же, что у входа. */
  requestChannelCode(input: AuthCodeInput): Promise<void>;
  /** Подтверждение второго канала. Наружу идёт обновлённый профиль из ответа. */
  confirmChannel(input: ChannelConfirmInput): Promise<CurrentUserData>;
  createInvitation(companyId: string, input: InvitationInput): Promise<Invitation>;
  listCompanyInvitations(companyId: string): Promise<readonly Invitation[]>;
  /**
   * Отзыв приглашения. Наружу не идёт ничего: контракт объявляет `204` **без
   * тела**, и объявленный тип обязан совпадать с ответом контракта.
   */
  revokeInvitation(companyId: string, invitationId: string): Promise<void>;
  resendInvitation(companyId: string, invitationId: string): Promise<Invitation>;
  /** Слои-источники: роль-ярлык и два персональных слоя, как их отдал сервер. */
  listMemberPermissions(companyId: string, membershipId: string): Promise<MemberPermissions>;
  grantMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions>;
  revokeMemberGrant(companyId: string, membershipId: string, right: Permission): Promise<void>;
  denyMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions>;
  revokeMemberDeny(companyId: string, membershipId: string, right: Permission): Promise<void>;
  listCompanyDocuments(companyId: string): Promise<readonly Document[]>;
  listMemberDocuments(companyId: string, membershipId: string): Promise<readonly Document[]>;
  /** Временная подписанная ссылка. Годность её решает сервер. */
  issueDocumentLink(documentId: string): Promise<DocumentLink>;

  // Операции домена транспорта — блок `WEB-1.3`. Перечень слоя по-прежнему
  // закрыт целиком и сверяется с описью операций контракта. Операций решения
  // по заявке, правки и удаления сервисной записи здесь нет и появиться не
  // может — контракт их не объявляет вовсе: журнал только пополняется, а
  // решение по заявке — предмет Admin.
  /**
   * Реестр транспорта фирмы — каждая запись как пришла, выведенные тоже, с
   * признаком назначаемости и его причиной (их считает сервер). `work_id` —
   * назначаемость для этой работы (блок `WEB-2.2`): уходит, только когда
   * задан; реестр зовёт операцию без него, как прежде.
   */
  listCompanyVehicles(companyId: string, options?: { work_id?: string }): Promise<readonly FleetVehicle[]>;
  /**
   * Добавление транспорта. Повтор своего VIN возвращает ту же запись со
   * статусом 200 — идемпотентность по естественному ключу, заголовок ей не
   * нужен; клиент повтора не подавляет и формата VIN не проверяет.
   */
  addCompanyVehicle(companyId: string, input: VehicleCreateRequest): Promise<Vehicle>;
  /** Вывод из реестра: запись закрывается датой. `204` без тела, повтор законен. */
  withdrawCompanyVehicle(companyId: string, vehicleId: string): Promise<void>;
  /**
   * Перевод состояния цикла ремонта. Перечень переходов живёт на сервере:
   * клиент шлёт выбранное и рисует ответ — тот несёт запись целиком.
   */
  changeVehicleRepairState(companyId: string, vehicleId: string, input: RepairStateChangeRequest): Promise<Vehicle>;
  /** Сервисная история — журнал только пополняется, вывод её не трогает. */
  listVehicleServiceRecords(companyId: string, vehicleId: string): Promise<readonly ServiceRecord[]>;
  /**
   * Сервисная запись. Идемпотентность — заголовком: ключ рождается здесь на
   * каждую отправку заново, две одинаковые записи с разными ключами законны.
   */
  addVehicleServiceRecord(companyId: string, vehicleId: string, input: ServiceRecordCreateRequest): Promise<ServiceRecord>;
  /** Заявки обеих сторон одним списком: сторона видна полем direction. */
  listCompanyVinClaims(companyId: string): Promise<readonly VinClaim[]>;
  /**
   * Заявка на освобождение занятого VIN — реквизит техпаспорта строкой и файл
   * техпаспорта (блок `WEB-2.4`, контракт `0.9.33`: тело — `multipart`).
   * Повтор живой заявки возвращает её же со статусом 200; клиент не подавляет.
   */
  createVinClaim(companyId: string, input: VinClaimSubmission): Promise<VinClaim>;
  /** Возражение держателя. Окно считает сервер: после окна — 422 от него. */
  objectVinClaim(companyId: string, claimId: string): Promise<VinClaim>;

  // Операция блока `WEB-3.2` — страница публичного отслеживания по ссылке с
  // PIN. Живёт в том же интерфейсе слоя: второй путь отправки рядом с общим
  // означал бы второе место, где решается, какие заголовки уходят.
  /**
   * Просмотр работы по публичной ссылке. Входа нет: слой страницы создаётся
   * без токена, и язык уходит заголовком гостя. Неверный PIN, запирание и
   * «ссылка не действует» — отказы сервера с машинным кодом; попыток клиент не
   * считает и ничего не запирает сам.
   */
  viewPublicTracking(token: string, pin: string): Promise<PublicTracking>;

  // Операции блока `WEB-2.1` — кабинет логиста: объявления о грузе фирмы,
  // входящие offer'ы и доска слотов, плюс два справочника формы. Перечень слоя
  // по-прежнему закрыт целиком и сверяется с описью операций контракта;
  // операции чтения работы здесь нет намеренно — доска показывает номер работы
  // из слота и работ не запрашивает (решение формы Р3 А).
  /** Подсказка городов по строке — в порядке ответа, именами на языке ответа. */
  listCities(query: { q: string }): Promise<readonly City[]>;
  /**
   * Справочник кузовов: тип, подтипы и применимые параметры. Матрица
   * применимости живёт на сервере, и у клиента своей нет.
   */
  listBodyTypes(): Promise<readonly BodyTypeInfo[]>;
  /**
   * Публикация объявления. Ключ идемпотентности приносит форма: повтор
   * отправки до ответа обязан уйти **тем же** ключом (решение формы Б5), а
   * знает о повторе только она. Рождается ключ по-прежнему в одном месте —
   * `newIdempotencyKey` этого файла.
   */
  createCargoListing(companyId: string, input: CargoListingSubmission, idempotencyKey: string): Promise<CargoListing>;
  /** Объявления фирмы во всех статусах, новые сверху, — страницей курсора. */
  listCompanyCargoListings(companyId: string, options?: { cursor?: string }): Promise<PageEnvelope<CargoListing>>;
  /** Объявление целиком: км, транзит, `manageable` и цену считает сервер. */
  getCargoListing(listingId: string): Promise<CargoListing>;
  /** Правка объявления целиком — та же форма тела, без ключа идемпотентности. */
  updateCargoListing(listingId: string, input: CargoListingSubmission): Promise<CargoListing>;
  /** Закрытие объявления. Повтор над закрытым сервер принимает тем же телом. */
  closeCargoListing(listingId: string): Promise<CargoListing>;
  /** Offer'ы объявления во всех статусах, новые сверху, — страницей курсора. */
  listCargoListingOffers(listingId: string, options?: { cursor?: string }): Promise<PageEnvelope<CargoOffer>>;
  /** Одобрение offer'а. Истёк ли он и хватает ли слотов, решает сервер. */
  acceptCargoOffer(offerId: string): Promise<CargoOffer>;
  /** Отклонение offer'а — без тела: причины CR-004 §5 не берёт. */
  declineCargoOffer(offerId: string): Promise<CargoOffer>;
  /** Слоты заказа в порядке ответа: номер, статус, offer и работа слота. */
  listCargoSlots(listingId: string): Promise<readonly CargoSlot[]>;

  // Операции блока `WEB-2.4` — кабинет механика: карточка машины, её
  // неисправности и фото, водители фирмы. Перечень слоя по-прежнему закрыт
  // целиком и сверяется с описью операций контракта; правки и удаления
  // неисправности здесь нет и появиться не может — запись неизменяема, и
  // контракт таких операций не объявляет.
  /**
   * Карточка машины: запись реестра, фото и открытые неисправности. Состояние
   * машины и закрытие неисправностей решает сервер — карточка их показывает.
   */
  getCompanyVehicle(companyId: string, vehicleId: string): Promise<VehicleCard>;
  /** Полная история неисправностей машины в порядке ответа, закрытые тоже. */
  listVehicleFaults(companyId: string, vehicleId: string): Promise<readonly VehicleFault[]>;
  /**
   * Запись неисправности — `multipart` с ключом идемпотентности формы: повтор
   * до ответа несёт тот же ключ (решение формы Б9), а знает о повторе только
   * она. Переводит ли запись машину в «неисправность выявлена», решает сервер.
   */
  addVehicleFault(companyId: string, vehicleId: string, input: VehicleFaultSubmission, idempotencyKey: string): Promise<VehicleFault>;
  /**
   * Фото машины — одна часть `file` за запрос, с ключом идемпотентности формы.
   * Файл `null` — не выбран: части нет, и отказ даёт сервер (`422`).
   */
  addVehiclePhoto(companyId: string, vehicleId: string, file: File | null, idempotencyKey: string): Promise<VehiclePhoto>;
  /** Удаление фото машины — `204` без тела, ряд тот же, что вывод из реестра. */
  deleteVehiclePhoto(companyId: string, vehicleId: string, photoId: string): Promise<void>;
  /**
   * Водители фирмы: имя, возраст и пол — и ничего сверх. Возраст считает
   * сервер, кто вправе читать — тоже он (`403`).
   */
  listCompanyDrivers(companyId: string): Promise<readonly CompanyDriver[]>;

  // Операции блока `WEB-2.3` — кабинет бухгалтера: расходы и выплаты фирмы,
  // работа подписью строки. Перечень слоя по-прежнему закрыт целиком и
  // сверяется с описью операций контракта; итогов и выгрузки здесь нет и
  // появиться не может — контракт их не объявляет (развилка Р7 А). Кто вправе
  // вести учёт, решает сервер (`403`), не слой.
  /** Работа целиком — подпись строки учёта: название и статус от сервера. */
  getWork(workId: string): Promise<Work>;
  /** Расходы фирмы страницей курсора, новые сверху, — отбор параметрами запроса. */
  listCompanyExpenses(companyId: string, options?: ExpensesQuery): Promise<PageEnvelope<CompanyExpense>>;
  /**
   * Запись расхода — `multipart` с ключом идемпотентности формы: повтор до
   * ответа несёт тот же ключ (решение формы `Б5`), а знает о повторе только она.
   */
  createCompanyExpense(companyId: string, input: CompanyExpenseSubmission, idempotencyKey: string): Promise<CompanyExpense>;
  /** Правка расхода — та же форма `multipart` методом `POST`, без ключа. */
  updateCompanyExpense(expenseId: string, input: CompanyExpenseSubmission): Promise<CompanyExpense>;
  /** Удаление расхода — мягкое на сервере, `204` без тела. */
  deleteCompanyExpense(expenseId: string): Promise<void>;
  /** Выплаты фирмы страницей курсора, новые сверху, — отбор параметрами запроса. */
  listCompanyPayouts(companyId: string, options?: PayoutsQuery): Promise<PageEnvelope<CompanyPayout>>;
  /** Запись выплаты — тело целиком, ключ идемпотентности формы. */
  createCompanyPayout(companyId: string, input: CompanyPayoutSubmission, idempotencyKey: string): Promise<CompanyPayout>;
  /** Правка выплаты — `PUT` тем же телом целиком, без ключа. */
  updateCompanyPayout(payoutId: string, input: CompanyPayoutSubmission): Promise<CompanyPayout>;
  /** Удаление выплаты — мягкое на сервере, `204` без тела. */
  deleteCompanyPayout(payoutId: string): Promise<void>;

  // Операции блока `WEB-2.2` — кабинет диспетчера: лента грузов и сводка
  // городов отправления, подача offer'а и «Мои предложения», работа,
  // назначение транспорта и водителя. `getWork` и `listCompanyDrivers` —
  // одной копией выше, у блоков `WEB-2.3` и `WEB-2.4` (подпись общая).
  // Перечень слоя по-прежнему закрыт целиком и сверяется с описью операций
  // контракта;
  // карточки offer'а здесь нет — список и ответ действия несут его целиком.
  /**
   * Лента грузов страницей курсора. Фильтры уходят ровно именами контракта и
   * только заданными; ни отбора, ни пересортировки — порядок сервера.
   */
  listCargoListings(filters: CargoFeedFilters, options?: { cursor?: string }): Promise<PageEnvelope<CargoListing>>;
  /**
   * Сводка городов отправления с теми же фильтрами. Ни страницы, ни курсора:
   * у операции их нет, и сервер отвечает на них `422`.
   */
  listCargoListingMapCities(filters: CargoFeedFilters): Promise<ListingMapCityList>;
  /**
   * Подача offer'а. Ключ идемпотентности приносит форма: повтор до ответа
   * обязан уйти тем же ключом (решение формы Б10), а знает о повторе только
   * она. Право подачи, свободные машины и порог цены судит сервер.
   */
  createCargoOffer(listingId: string, input: CargoOfferSubmission, idempotencyKey: string): Promise<CargoOffer>;
  /** Offer'ы фирмы на грузы во всех статусах — страницей курсора. */
  listCompanyCargoOffers(companyId: string, options?: { cursor?: string }): Promise<PageEnvelope<CargoOffer>>;
  /** Подтверждение одобренного offer'а. Срок судит сервер (`409`). */
  confirmCargoOffer(offerId: string): Promise<CargoOffer>;
  /** Отзыв ждущего или одобренного offer'а — без тела. */
  withdrawCargoOffer(offerId: string): Promise<CargoOffer>;
  /**
   * Назначение и замена транспорта и водителя — `PUT` ровно тремя полями.
   * Условия допуска проверяет guard сервера и отвечает пачкой `422`.
   */
  assignWork(workId: string, input: WorkAssignmentInput): Promise<Work>;
}

const AUTH_CODE_PATH = '/v1/auth/request-code';
const AUTH_VERIFY_PATH = '/v1/auth/verify';
const CURRENT_USER_PATH = '/v1/me';
const COMPANIES_PATH = '/v1/companies';
const COMPANY_MEMBERS_SEGMENT = '/members';
const WORKS_PATH = '/v1/works';

// Адреса кабинета — блок `WEB-1.2`. Названы здесь по одному разу, как и
// прежние: второй экземпляр формы адреса перестанет разбираться в тот день,
// когда форма изменится.
const SESSIONS_PATH = '/v1/sessions';
const CHANNEL_CODE_PATH = '/v1/me/channels/request-code';
const CHANNEL_CONFIRM_PATH = '/v1/me/channels/confirm';
const DOCUMENTS_PATH = '/v1/documents';
const DOCUMENTS_SEGMENT = '/documents';
const INVITATIONS_SEGMENT = '/invitations';
const RIGHTS_SEGMENT = '/permissions';

// Адреса домена транспорта — блок `WEB-1.3`. Названы здесь по одному разу,
// рядом с прежними, и по той же причине.
const VEHICLES_SEGMENT = '/vehicles';
const REPAIR_STATE_SEGMENT = '/repair-state';
const SERVICE_RECORDS_SEGMENT = '/service-records';
const VIN_CLAIMS_SEGMENT = '/vin-claims';
const OBJECTION_SEGMENT = '/objection';

// Адреса кабинета механика — блок `WEB-2.4`. Названы здесь по одному разу.
const FAULTS_SEGMENT = '/faults';
const PHOTOS_SEGMENT = '/photos';
const DRIVERS_SEGMENT = '/drivers';

// Адреса кабинета бухгалтера — блок `WEB-2.3`. Названы здесь по одному разу.
// Правка и удаление записи живут вне адреса фирмы — так их объявляет контракт.
const EXPENSES_SEGMENT = '/expenses';
const PAYOUTS_SEGMENT = '/payouts';
const COMPANY_EXPENSES_PATH = '/v1/company-expenses';
const COMPANY_PAYOUTS_PATH = '/v1/company-payouts';

// Адрес просмотра по публичной ссылке — блок `WEB-3.2`. Назван здесь один раз.
const PUBLIC_TRACKING_PATH = '/v1/public/tracking';

// Адреса кабинета логиста — блок `WEB-2.1`. Названы здесь по одному разу.
const CITIES_PATH = '/v1/cities';
const BODY_TYPES_PATH = '/v1/body-types';
const CARGO_LISTINGS_PATH = '/v1/cargo-listings';
const CARGO_LISTINGS_SEGMENT = '/cargo-listings';
const CARGO_OFFERS_PATH = '/v1/cargo-offers';
const OFFERS_SEGMENT = '/offers';
const SLOTS_SEGMENT = '/slots';

// Адреса кабинета диспетчера — блок `WEB-2.2`. Названы здесь по одному разу.
const CARGO_LISTINGS_MAP_PATH = '/v1/cargo-listings/map';
const CARGO_OFFERS_SEGMENT = '/cargo-offers';
const ASSIGNMENT_SEGMENT = '/assignment';
// `DRIVERS_SEGMENT` — одной копией выше, у блока `WEB-2.4`.

/** Заголовок языка гостя. Называется в треке один раз — здесь. */
const LANGUAGE_HEADER = 'accept-language';

/** Заголовок идемпотентности создающей операции. Назван один раз — здесь. */
const IDEMPOTENCY_HEADER = 'Idempotency-Key';

// Имена частей встроенных форм `multipart` — блок `WEB-2.4`. Массив фото
// неисправности уходит повтором части `photos[]` (соглашение проекта, поправка
// CONTRACT К1); отказ по фото сервер называет ключом `photos.N`.
const COMMENT_PART = 'comment';
const FAULT_PHOTO_PART = 'photos[]';
const VEHICLE_PHOTO_PART = 'file';
const VIN_PART = 'vin';
const TECH_PASSPORT_NUMBER_PART = 'tech_passport_number';
const TECH_PASSPORT_FILE_PART = 'tech_passport_file';

// Части формы расхода — блок `WEB-2.3`, имена контракта. Чек — одна часть.
const EXPENSE_CATEGORY_PART = 'category';
const EXPENSE_AMOUNT_PART = 'amount';
const EXPENSE_CURRENCY_PART = 'currency';
const EXPENSE_SPENT_ON_PART = 'spent_on';
const EXPENSE_WORK_PART = 'work_id';
const EXPENSE_VEHICLE_PART = 'vehicle_id';
const EXPENSE_RECEIPT_PART = 'receipt';

/**
 * Ключ идемпотентности — **единственное** место его рождения.
 *
 * Ключ рождается в момент отправки, и на новую отправку рождается новый:
 * переиспользованный ключ склеил бы две разные записи, а две одинаковые
 * записи законны — так объявляет контракт. Придумывает ключ клиент.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function withoutTrailingSlash(value: string): string {
  let trimmed = value.trim();

  while (trimmed.endsWith('/')) {
    trimmed = trimmed.slice(0, -1);
  }

  return trimmed;
}

/** Адрес фирмы. Единственное место, где он собирается. */
function companyPath(companyId: string): string {
  return `${COMPANIES_PATH}/${encodeURIComponent(companyId)}`;
}

/** Адрес объявления о грузе. Тот же единственный сбор. */
function cargoListingPath(listingId: string): string {
  return `${CARGO_LISTINGS_PATH}/${encodeURIComponent(listingId)}`;
}

/** Адрес offer'а на груз. Тот же единственный сбор. */
function cargoOfferPath(offerId: string): string {
  return `${CARGO_OFFERS_PATH}/${encodeURIComponent(offerId)}`;
}

/**
 * Адрес с курсором страницы. Курсор непрозрачен: он уходит как пришёл, и
 * разбирать его клиенту нечем.
 */
function withCursor(path: string, cursor?: string): string {
  if (cursor === undefined) {
    return path;
  }

  return `${path}?${new URLSearchParams({ cursor }).toString()}`;
}

/** Адрес работы. Тот же единственный сбор. */
function workPath(workId: string): string {
  return `${WORKS_PATH}/${encodeURIComponent(workId)}`;
}

/** Адрес записи расхода — правка и удаление. Тот же единственный сбор. */
function companyExpensePath(expenseId: string): string {
  return `${COMPANY_EXPENSES_PATH}/${encodeURIComponent(expenseId)}`;
}

/** Адрес записи выплаты — правка и удаление. Тот же единственный сбор. */
function companyPayoutPath(payoutId: string): string {
  return `${COMPANY_PAYOUTS_PATH}/${encodeURIComponent(payoutId)}`;
}

/**
 * Части формы расхода в порядке контракта: пять всегда, машина, комментарий и
 * чек — только когда даны (решение формы `Б7`).
 */
function expenseParts(input: CompanyExpenseSubmission): MultipartPart[] {
  const parts: MultipartPart[] = [
    [EXPENSE_CATEGORY_PART, input.category],
    [EXPENSE_AMOUNT_PART, input.amount],
    [EXPENSE_CURRENCY_PART, input.currency],
    [EXPENSE_SPENT_ON_PART, input.spent_on],
    [EXPENSE_WORK_PART, input.work_id],
  ];

  if (input.vehicle_id !== null) {
    parts.push([EXPENSE_VEHICLE_PART, input.vehicle_id]);
  }

  if (input.comment !== null) {
    parts.push([COMMENT_PART, input.comment]);
  }

  if (input.receipt !== null) {
    parts.push([EXPENSE_RECEIPT_PART, input.receipt]);
  }

  return parts;
}

/**
 * Адрес со строкой запроса — только из заданных параметров. Повторяемый
 * параметр (типы кузова) уходит по одному на каждое значение; незаданный не
 * уходит вовсе: умолчаний у фильтров нет, и «не задано» — это отсутствие
 * параметра, а не пустая строка или ложь.
 */
function withQuery(
  path: string,
  query: Readonly<Record<string, string | number | boolean | readonly string[] | undefined>>,
): string {
  const params = new URLSearchParams();

  for (const [name, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }

    if (typeof value === 'object') {
      for (const item of value) {
        params.append(name, item);
      }

      continue;
    }

    params.append(name, String(value));
  }

  const encoded = params.toString();

  return encoded === '' ? path : `${path}?${encoded}`;
}

/** Адрес записи членства внутри фирмы. Тот же единственный сбор. */
function membershipPath(companyId: string, membershipId: string): string {
  return `${companyPath(companyId)}${COMPANY_MEMBERS_SEGMENT}/${encodeURIComponent(membershipId)}`;
}

/** Адрес записи реестра транспорта внутри фирмы. Тот же единственный сбор. */
function vehiclePath(companyId: string, vehicleId: string): string {
  return `${companyPath(companyId)}${VEHICLES_SEGMENT}/${encodeURIComponent(vehicleId)}`;
}

/** Адрес заявки на освобождение VIN внутри фирмы. Тот же единственный сбор. */
function vinClaimPath(companyId: string, claimId: string): string {
  return `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}/${encodeURIComponent(claimId)}`;
}

/**
 * Адрес слоёв прав записи членства.
 *
 * Хвост слоя приходит значением: два слоя различаются одним сегментом, и
 * второй строитель рядом означал бы две формы одного адреса.
 */
function memberRightsPath(companyId: string, membershipId: string, tail: string): string {
  return `${membershipPath(companyId, membershipId)}${RIGHTS_SEGMENT}${tail}`;
}

async function readPayload(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const origin = withoutTrailingSlash(options.baseUrl);

  let token: string | null = null;
  let locale: User['locale'] | null = null;

  function failureOf(payload: unknown): ApiFailure {
    const code = errorCode(payload);
    const requestId = isErrorEnvelope(payload) ? payload.error.request_id : null;

    if (code === UNAUTHENTICATED_CODE) {
      // Вкладка, оставшаяся с отвергнутым токеном, повторяет отказ на каждом
      // действии, и «возврат на вход» выглядит работающим, не будучи им.
      token = null;
    }

    return new ApiFailure(code, requestId, errorDetails(payload), errorMessage(payload));
  }

  /**
   * Отправка и разбор ответа — одно место для обоих видов тела.
   *
   * Заголовки входа и языка решаются здесь и только здесь: второй путь
   * отправки рядом — второе место, где решается, какие заголовки уходят.
   * Тип тела `multipart` не ставится руками: границу частей пишет браузер, и
   * заголовок, заданный клиентом, остался бы без неё.
   */
  async function transmit<T>(
    path: string,
    method: string,
    body: string | FormData | undefined,
    extraHeaders?: Readonly<Record<string, string>>,
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json', ...extraHeaders };
    const init: RequestInit = { method, headers };

    if (token !== null) {
      headers.authorization = `Bearer ${token}`;
    } else if (locale !== null) {
      // Заголовок языка — только у гостя. Контракт говорит дословно: «заголовок
      // продолжает действовать только для неаутентифицированных запросов»,
      // потому что язык владельца токена определяет профиль.
      headers[LANGUAGE_HEADER] = locale;
    }

    if (typeof body === 'string') {
      headers['content-type'] = 'application/json';
    }

    if (body !== undefined) {
      init.body = body;
    }

    let response: Response;

    try {
      response = await fetch(`${origin}${path}`, init);
    } catch {
      // Обрыв связи приходит тем же отказом: необработанное исключение
      // транспорта оставляет на экране белое пятно вместо состояния отказа.
      throw new ApiFailure(null, null);
    }

    const payload = await readPayload(response);

    if (!response.ok) {
      throw failureOf(payload);
    }

    return payload as T;
  }

  async function send<T>(
    path: string,
    method: string,
    payload?: unknown,
    extraHeaders?: Readonly<Record<string, string>>,
  ): Promise<T> {
    return await transmit<T>(path, method, payload === undefined ? undefined : JSON.stringify(payload), extraHeaders);
  }

  /**
   * Тело `multipart` — **единственное** место его сборки в треке (блок
   * `WEB-2.4`, spec §4 п. 2). Части уходят в том порядке и с теми именами, что
   * дала операция; файл — как выбран: ни типа, ни размера, ни числа клиент не
   * проверяет.
   */
  async function sendMultipart<T>(
    path: string,
    parts: readonly MultipartPart[],
    extraHeaders?: Readonly<Record<string, string>>,
  ): Promise<T> {
    const form = new FormData();

    for (const [name, value] of parts) {
      form.append(name, value);
    }

    return await transmit<T>(path, 'POST', form, extraHeaders);
  }

  return {
    get token(): string | null {
      return token;
    },

    useLocale(chosen: User['locale']): void {
      locale = chosen;
    },

    /**
     * Запрос кода подтверждения.
     *
     * Наружу не отдаётся ничего: срок кода и срок повторной отправки приходят
     * в ответе, но поведения вокруг них у блока нет, а число на экране без
     * поведения закрывает требование на вид.
     */
    async requestAuthCode(input: AuthCodeInput): Promise<void> {
      await send<unknown>(AUTH_CODE_PATH, 'POST', input);
    },

    async verifyAuthCode(input: AuthVerifyInput): Promise<AuthSession> {
      const envelope = await send<DataEnvelope<AuthSession>>(AUTH_VERIFY_PATH, 'POST', input);
      const session = unwrap(envelope);

      token = session.token;

      return session;
    },

    /** Профиль вместе с членствами: имя фирмы приезжает вложением. */
    async getCurrentUser(): Promise<CurrentUserData> {
      return unwrap(await send<DataEnvelope<CurrentUserData>>(CURRENT_USER_PATH, 'GET'));
    },

    /**
     * Смена языка вошедшего. Наружу отдаётся ответ сервера целиком: источник
     * истины — профиль, а не выбранное значение.
     */
    async updateCurrentUser(input: UserUpdateRequest): Promise<CurrentUserData> {
      return unwrap(
        await send<DataEnvelope<CurrentUserData>>(CURRENT_USER_PATH, 'PATCH', input),
      );
    },

    async createCompany(input: CompanyCreateRequest): Promise<Company> {
      return unwrap(await send<DataEnvelope<Company>>(COMPANIES_PATH, 'POST', input));
    },

    /**
     * Состав фирмы, чей идентификатор дан адресом.
     *
     * Ни отбора, ни пересортировки: из чего складывается действующее участие —
     * правило сервера, и копия условия здесь разошлась бы с ним молча.
     */
    async listCompanyMembers(companyId: string): Promise<readonly CompanyMember[]> {
      const path = `${COMPANIES_PATH}/${encodeURIComponent(companyId)}${COMPANY_MEMBERS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<CompanyMember[]>>(path, 'GET'));
    },

    /**
     * Страница ленты в форме контракта: работы и метаданные курсора.
     *
     * Ни отбора, ни пересортировки: порядок задаёт сервер тем же курсором,
     * которым нарезал страницы, а правила видимости нет ни у кого.
     */
    async listWorks(options?: WorksQuery): Promise<PageEnvelope<Work>> {
      // Ключи отбора — имена параметров запроса контракта (`WorksQuery`), и
      // уходят они как даны: перечень закрыт типом, а значение судит сервер.
      const query: Record<string, string> = {};

      for (const [name, value] of Object.entries(options ?? {})) {
        if (value !== undefined) {
          query[name] = String(value);
        }
      }

      return await send<PageEnvelope<Work>>(withQuery(WORKS_PATH, query), 'GET');
    },

    /**
     * Видимые сессии владельца токена.
     *
     * Признак «текущая» приходит полем ответа и клиентом не вычисляется:
     * «первая в списке» и «самая свежая» — догадки, которые сервер не обещал.
     */
    async listSessions(): Promise<readonly Session[]> {
      return unwrap(await send<DataEnvelope<Session[]>>(SESSIONS_PATH, 'GET'));
    },

    /**
     * Завершение сессии — любой, включая текущую.
     *
     * Чужая отвечает `404`, и различать её от несуществующей клиенту нечем:
     * это и есть нераскрытие существования чужого объекта.
     */
    async terminateSession(sessionId: string): Promise<void> {
      await send<unknown>(`${SESSIONS_PATH}/${encodeURIComponent(sessionId)}`, 'DELETE');
    },

    /**
     * Код на второй канал связи.
     *
     * Форма тела — та же, что у запроса кода входа: ключ канала ровно один, и
     * какой именно, выбирает клиент. Проверок формата здесь нет ни одной.
     */
    async requestChannelCode(input: AuthCodeInput): Promise<void> {
      await send<unknown>(CHANNEL_CODE_PATH, 'POST', input);
    },

    /**
     * Подтверждение второго канала.
     *
     * Наружу идёт **обновлённый профиль из ответа**: перерисовка по введённому
     * значению показала бы то, чего сервер, возможно, не принял.
     */
    async confirmChannel(input: ChannelConfirmInput): Promise<CurrentUserData> {
      return unwrap(await send<DataEnvelope<CurrentUserData>>(CHANNEL_CONFIRM_PATH, 'POST', input));
    },

    /**
     * Приглашение в фирму: роль и ровно один канал.
     *
     * Профиля приглашённому это не создаёт — приглашение адресовано каналу, а
     * вторую половину выполняет сам приглашённый своим токеном.
     */
    async createInvitation(companyId: string, input: InvitationInput): Promise<Invitation> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Invitation>>(path, 'POST', input));
    },

    /** Приглашения фирмы во всех статусах: отбора по статусу здесь нет. */
    async listCompanyInvitations(companyId: string): Promise<readonly Invitation[]> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Invitation[]>>(path, 'GET'));
    },

    /**
     * Отзыв приглашения — `204` **без тела**, как и у соседей того же рода.
     *
     * Развернуть конверт здесь нечего: `readPayload` на пустом теле отдаёт
     * `null`, и `unwrap(null)` бросил бы отказ на **удавшейся** операции —
     * сервер отозвал, а экран ушёл бы в состояние отказа целиком. Ряд один:
     * `terminateSession`, `revokeMemberGrant` и `revokeMemberDeny` объявлены
     * так же и `unwrap` не зовут.
     */
    async revokeInvitation(companyId: string, invitationId: string): Promise<void> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}/${encodeURIComponent(invitationId)}`;

      await send<unknown>(path, 'DELETE');
    },

    async resendInvitation(companyId: string, invitationId: string): Promise<Invitation> {
      const path = `${companyPath(companyId)}${INVITATIONS_SEGMENT}/${encodeURIComponent(invitationId)}/resend`;

      return unwrap(await send<DataEnvelope<Invitation>>(path, 'POST'));
    },

    /**
     * Слои-источники прав записи членства, как их отдал сервер.
     *
     * Действующих прав в ответе нет намеренно: их вычисляет сервер, и плоский
     * список задал бы неверную модель. Клиент не складывает слои ни здесь, ни
     * на экране.
     */
    async listMemberPermissions(companyId: string, membershipId: string): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '');

      return unwrap(await send<DataEnvelope<MemberPermissions>>(path, 'GET'));
    },

    async grantMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '/grants');

      return unwrap(
        await send<DataEnvelope<MemberPermissions>>(path, 'POST', { permission: right }),
      );
    },

    async revokeMemberGrant(companyId: string, membershipId: string, right: Permission): Promise<void> {
      const path = memberRightsPath(companyId, membershipId, `/grants/${encodeURIComponent(right)}`);

      await send<unknown>(path, 'DELETE');
    },

    async denyMemberPermission(companyId: string, membershipId: string, right: Permission): Promise<MemberPermissions> {
      const path = memberRightsPath(companyId, membershipId, '/denies');

      return unwrap(
        await send<DataEnvelope<MemberPermissions>>(path, 'POST', { permission: right }),
      );
    },

    async revokeMemberDeny(companyId: string, membershipId: string, right: Permission): Promise<void> {
      const path = memberRightsPath(companyId, membershipId, `/denies/${encodeURIComponent(right)}`);

      await send<unknown>(path, 'DELETE');
    },

    /** Документы фирмы. Статуса самой фирмы в ответе нет — его не существует. */
    async listCompanyDocuments(companyId: string): Promise<readonly Document[]> {
      const path = `${companyPath(companyId)}${DOCUMENTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Document[]>>(path, 'GET'));
    },

    /** Документы записи членства — той, что дана адресом, и никакой другой. */
    async listMemberDocuments(companyId: string, membershipId: string): Promise<readonly Document[]> {
      const path = `${membershipPath(companyId, membershipId)}${DOCUMENTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Document[]>>(path, 'GET'));
    },

    /**
     * Временная подписанная ссылка на файл документа.
     *
     * Ссылка просится в момент нажатия и не кэшируется: каждый вызов даёт свою
     * со своим сроком, а годность решает сервер — `expires_at` клиент ни с чем
     * не сравнивает.
     */
    async issueDocumentLink(documentId: string): Promise<DocumentLink> {
      const path = `${DOCUMENTS_PATH}/${encodeURIComponent(documentId)}/link`;

      return unwrap(await send<DataEnvelope<DocumentLink>>(path, 'POST'));
    },

    /**
     * Реестр транспорта фирмы — как отдал сервер, включая выведенные записи.
     *
     * Ни отбора, ни сортировки — по состоянию в том числе: «машина в ремонте
     * на рейс не назначается» есть серверный guard, а не вид списка.
     */
    async listCompanyVehicles(companyId: string, options?: { work_id?: string }): Promise<readonly FleetVehicle[]> {
      const path = withQuery(`${companyPath(companyId)}${VEHICLES_SEGMENT}`, { work_id: options?.work_id });

      return unwrap(await send<DataEnvelope<FleetVehicle[]>>(path, 'GET'));
    },

    /**
     * Добавление транспорта. Ответ — запись целиком: `201` — создана, `200` —
     * повтор своего VIN вернул уже существующую, и решил это сервер.
     */
    async addCompanyVehicle(companyId: string, input: VehicleCreateRequest): Promise<Vehicle> {
      const path = `${companyPath(companyId)}${VEHICLES_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Vehicle>>(path, 'POST', input));
    },

    /**
     * Вывод из реестра — `204` **без тела**, разворачивать нечего: ряд тот же,
     * что `terminateSession` и `revokeInvitation`. Повтор вывода законен.
     */
    async withdrawCompanyVehicle(companyId: string, vehicleId: string): Promise<void> {
      await send<unknown>(vehiclePath(companyId, vehicleId), 'DELETE');
    },

    /**
     * Перевод состояния цикла ремонта: одно поле тела, ответ — вся запись.
     *
     * Допустимости перехода клиент не предсказывает: перескок через шаг и
     * операция над выведенным транспортом отклоняются сервером статусом 422.
     */
    async changeVehicleRepairState(
      companyId: string,
      vehicleId: string,
      input: RepairStateChangeRequest,
    ): Promise<Vehicle> {
      const path = `${vehiclePath(companyId, vehicleId)}${REPAIR_STATE_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Vehicle>>(path, 'POST', input));
    },

    /** Журнал работ — в порядке ответа, без правки и удаления: операций нет. */
    async listVehicleServiceRecords(
      companyId: string,
      vehicleId: string,
    ): Promise<readonly ServiceRecord[]> {
      const path = `${vehiclePath(companyId, vehicleId)}${SERVICE_RECORDS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<ServiceRecord[]>>(path, 'GET'));
    },

    /**
     * Сервисная запись с заголовком идемпотентности.
     *
     * Ключ рождается на **каждую** отправку заново: две одинаковые записи
     * законны, и подавлять вторую клиенту нечем — естественного ключа у
     * операции нет, решает заголовок.
     */
    async addVehicleServiceRecord(
      companyId: string,
      vehicleId: string,
      input: ServiceRecordCreateRequest,
    ): Promise<ServiceRecord> {
      const path = `${vehiclePath(companyId, vehicleId)}${SERVICE_RECORDS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<ServiceRecord>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: newIdempotencyKey(),
        }),
      );
    },

    /** Заявки обеих сторон одним списком — сторона приходит полем ответа. */
    async listCompanyVinClaims(companyId: string): Promise<readonly VinClaim[]> {
      const path = `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VinClaim[]>>(path, 'GET'));
    },

    /**
     * Подача заявки — встроенная форма `multipart` из трёх частей: VIN,
     * реквизит техпаспорта и файл техпаспорта.
     *
     * ПЕРЕВОРОТ блока `WEB-2.4` (контракт `0.9.33`). Было: «ровно два поля
     * тела, поля файла нет — недостача решение автора контракта». Стало: автор
     * контракта файл добавил, и JSON-тело сервер больше не принимает. Файла
     * нет — части нет: обязательность судит сервер.
     */
    async createVinClaim(companyId: string, input: VinClaimSubmission): Promise<VinClaim> {
      const path = `${companyPath(companyId)}${VIN_CLAIMS_SEGMENT}`;
      const parts: MultipartPart[] = [
        [VIN_PART, input.vin],
        [TECH_PASSPORT_NUMBER_PART, input.tech_passport_number],
      ];

      if (input.tech_passport_file !== null) {
        parts.push([TECH_PASSPORT_FILE_PART, input.tech_passport_file]);
      }

      return unwrap(await sendMultipart<DataEnvelope<VinClaim>>(path, parts));
    },

    /**
     * Возражение держателя — без тела: заявку называет адрес. Окно считает
     * сервер: возражение после окна отклоняется статусом 422 от него.
     */
    async objectVinClaim(companyId: string, claimId: string): Promise<VinClaim> {
      const path = `${vinClaimPath(companyId, claimId)}${OBJECTION_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VinClaim>>(path, 'POST'));
    },

    /**
     * Просмотр по ссылке: токен — в пути, кодированным; PIN — **только** телом
     * из одного поля. В адресе PIN оказался бы в журналах посредников и в
     * истории браузера, а тело туда не попадает.
     */
    async viewPublicTracking(linkToken: string, pin: string): Promise<PublicTracking> {
      const path = `${PUBLIC_TRACKING_PATH}/${encodeURIComponent(linkToken)}`;

      return unwrap(await send<DataEnvelope<PublicTracking>>(path, 'POST', { pin }));
    },

    /** Подсказка городов: строка поиска уходит как введена, годность судит сервер. */
    async listCities(query: { q: string }): Promise<readonly City[]> {
      const path = `${CITIES_PATH}?${new URLSearchParams({ q: query.q }).toString()}`;

      return unwrap(await send<DataEnvelope<City[]>>(path, 'GET'));
    },

    /** Справочник кузовов в порядке ответа: названия — на языке ответа. */
    async listBodyTypes(): Promise<readonly BodyTypeInfo[]> {
      return unwrap(await send<DataEnvelope<BodyTypeInfo[]>>(BODY_TYPES_PATH, 'GET'));
    },

    /**
     * Публикация объявления с ключом идемпотентности формы: повтор до ответа
     * несёт тот же ключ, и сервер не заводит второго объявления.
     */
    async createCargoListing(
      companyId: string,
      input: CargoListingSubmission,
      idempotencyKey: string,
    ): Promise<CargoListing> {
      const path = `${companyPath(companyId)}${CARGO_LISTINGS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<CargoListing>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: idempotencyKey,
        }),
      );
    },

    /** Страница объявлений фирмы: ни отбора, ни пересортировки — порядок сервера. */
    async listCompanyCargoListings(
      companyId: string,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoListing>> {
      const path = withCursor(`${companyPath(companyId)}${CARGO_LISTINGS_SEGMENT}`, options?.cursor);

      return await send<PageEnvelope<CargoListing>>(path, 'GET');
    },

    async getCargoListing(listingId: string): Promise<CargoListing> {
      return unwrap(await send<DataEnvelope<CargoListing>>(cargoListingPath(listingId), 'GET'));
    },

    /** Правка целиком: та же форма тела; допустимость правки решает сервер (`409`). */
    async updateCargoListing(listingId: string, input: CargoListingSubmission): Promise<CargoListing> {
      return unwrap(await send<DataEnvelope<CargoListing>>(cargoListingPath(listingId), 'PUT', input));
    },

    async closeCargoListing(listingId: string): Promise<CargoListing> {
      const path = `${cargoListingPath(listingId)}/close`;

      return unwrap(await send<DataEnvelope<CargoListing>>(path, 'POST'));
    },

    /** Страница offer'ов объявления в порядке сервера, истёкшие и отклонённые тоже. */
    async listCargoListingOffers(
      listingId: string,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoOffer>> {
      const path = withCursor(`${cargoListingPath(listingId)}${OFFERS_SEGMENT}`, options?.cursor);

      return await send<PageEnvelope<CargoOffer>>(path, 'GET');
    },

    async acceptCargoOffer(offerId: string): Promise<CargoOffer> {
      const path = `${cargoOfferPath(offerId)}/accept`;

      return unwrap(await send<DataEnvelope<CargoOffer>>(path, 'POST'));
    },

    /** Отклонение без тела: адрес называет offer, причины форма не берёт. */
    async declineCargoOffer(offerId: string): Promise<CargoOffer> {
      const path = `${cargoOfferPath(offerId)}/decline`;

      return unwrap(await send<DataEnvelope<CargoOffer>>(path, 'POST'));
    },

    async listCargoSlots(listingId: string): Promise<readonly CargoSlot[]> {
      const path = `${cargoListingPath(listingId)}${SLOTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<CargoSlot[]>>(path, 'GET'));
    },

    /** Страница ленты грузов с фильтрами и курсором — порядок сервера. */
    async listCargoListings(
      filters: CargoFeedFilters,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoListing>> {
      const path = withQuery(CARGO_LISTINGS_PATH, { ...filters, cursor: options?.cursor });

      return await send<PageEnvelope<CargoListing>>(path, 'GET');
    },

    /** Сводка городов — конвертом целиком: «всего» живёт в `meta`, а не суммой. */
    async listCargoListingMapCities(filters: CargoFeedFilters): Promise<ListingMapCityList> {
      return await send<ListingMapCityList>(withQuery(CARGO_LISTINGS_MAP_PATH, filters), 'GET');
    },

    async createCargoOffer(
      listingId: string,
      input: CargoOfferSubmission,
      idempotencyKey: string,
    ): Promise<CargoOffer> {
      const path = `${cargoListingPath(listingId)}${OFFERS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<CargoOffer>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: idempotencyKey,
        }),
      );
    },

    async listCompanyCargoOffers(
      companyId: string,
      options?: { cursor?: string },
    ): Promise<PageEnvelope<CargoOffer>> {
      const path = withCursor(`${companyPath(companyId)}${CARGO_OFFERS_SEGMENT}`, options?.cursor);

      return await send<PageEnvelope<CargoOffer>>(path, 'GET');
    },

    async confirmCargoOffer(offerId: string): Promise<CargoOffer> {
      return unwrap(await send<DataEnvelope<CargoOffer>>(`${cargoOfferPath(offerId)}/confirm`, 'POST'));
    },

    async withdrawCargoOffer(offerId: string): Promise<CargoOffer> {
      return unwrap(await send<DataEnvelope<CargoOffer>>(`${cargoOfferPath(offerId)}/withdraw`, 'POST'));
    },

    /** Ответ — работа целиком: экран рисует привязку и историю из него. */
    async assignWork(workId: string, input: WorkAssignmentInput): Promise<Work> {
      const path = `${workPath(workId)}${ASSIGNMENT_SEGMENT}`;

      return unwrap(await send<DataEnvelope<Work>>(path, 'PUT', input));
    },

    /** Карточка машины целиком: состояние и открытые неисправности — от сервера. */
    async getCompanyVehicle(companyId: string, vehicleId: string): Promise<VehicleCard> {
      return unwrap(await send<DataEnvelope<VehicleCard>>(vehiclePath(companyId, vehicleId), 'GET'));
    },

    /** История неисправностей в порядке ответа: ни отбора, ни пересортировки. */
    async listVehicleFaults(companyId: string, vehicleId: string): Promise<readonly VehicleFault[]> {
      const path = `${vehiclePath(companyId, vehicleId)}${FAULTS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<VehicleFault[]>>(path, 'GET'));
    },

    /**
     * Запись неисправности: часть `comment` и по части `photos[]` на каждый
     * выбранный файл — все, как выбраны. Ключ идемпотентности приносит форма.
     */
    async addVehicleFault(
      companyId: string,
      vehicleId: string,
      input: VehicleFaultSubmission,
      idempotencyKey: string,
    ): Promise<VehicleFault> {
      const path = `${vehiclePath(companyId, vehicleId)}${FAULTS_SEGMENT}`;
      const parts: MultipartPart[] = [[COMMENT_PART, input.comment]];

      for (const photo of input.photos) {
        parts.push([FAULT_PHOTO_PART, photo]);
      }

      return unwrap(
        await sendMultipart<DataEnvelope<VehicleFault>>(path, parts, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
      );
    },

    /** Фото машины: единственная часть `file`. Седьмое, тип и размер судит сервер. */
    async addVehiclePhoto(
      companyId: string,
      vehicleId: string,
      file: File | null,
      idempotencyKey: string,
    ): Promise<VehiclePhoto> {
      const path = `${vehiclePath(companyId, vehicleId)}${PHOTOS_SEGMENT}`;
      const parts: MultipartPart[] = file === null ? [] : [[VEHICLE_PHOTO_PART, file]];

      return unwrap(
        await sendMultipart<DataEnvelope<VehiclePhoto>>(path, parts, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
      );
    },

    /** Удаление фото машины — `204` без тела: разворачивать нечего. */
    async deleteVehiclePhoto(companyId: string, vehicleId: string, photoId: string): Promise<void> {
      const path = `${vehiclePath(companyId, vehicleId)}${PHOTOS_SEGMENT}/${encodeURIComponent(photoId)}`;

      await send<unknown>(path, 'DELETE');
    },

    /** Водители фирмы в порядке ответа: ни отбора, ни пересортировки. */
    async listCompanyDrivers(companyId: string): Promise<readonly CompanyDriver[]> {
      const path = `${companyPath(companyId)}${DRIVERS_SEGMENT}`;

      return unwrap(await send<DataEnvelope<CompanyDriver[]>>(path, 'GET'));
    },

    /** Работа целиком: видима ли она смотрящему, решает сервер (`404`). */
    async getWork(workId: string): Promise<Work> {
      return unwrap(await send<DataEnvelope<Work>>(workPath(workId), 'GET'));
    },

    /** Страница расходов: ни отбора, ни пересортировки — отбор уходит параметрами. */
    async listCompanyExpenses(companyId: string, options?: ExpensesQuery): Promise<PageEnvelope<CompanyExpense>> {
      const path = withQuery(`${companyPath(companyId)}${EXPENSES_SEGMENT}`, {
        cursor: options?.cursor,
        work_id: options?.work_id,
        vehicle_id: options?.vehicle_id,
      });

      return await send<PageEnvelope<CompanyExpense>>(path, 'GET');
    },

    /**
     * Запись расхода: части формы контракта и ключ идемпотентности формы.
     * Принадлежность работы и машины фирме, тип и размер чека судит сервер.
     */
    async createCompanyExpense(
      companyId: string,
      input: CompanyExpenseSubmission,
      idempotencyKey: string,
    ): Promise<CompanyExpense> {
      const path = `${companyPath(companyId)}${EXPENSES_SEGMENT}`;

      return unwrap(
        await sendMultipart<DataEnvelope<CompanyExpense>>(path, expenseParts(input), {
          [IDEMPOTENCY_HEADER]: idempotencyKey,
        }),
      );
    },

    /** Правка расхода — `POST` на адрес записи той же формой; ключа нет. */
    async updateCompanyExpense(expenseId: string, input: CompanyExpenseSubmission): Promise<CompanyExpense> {
      return unwrap(await sendMultipart<DataEnvelope<CompanyExpense>>(companyExpensePath(expenseId), expenseParts(input)));
    },

    /** Удаление расхода — `204` без тела: разворачивать нечего. */
    async deleteCompanyExpense(expenseId: string): Promise<void> {
      await send<unknown>(companyExpensePath(expenseId), 'DELETE');
    },

    /** Страница выплат: ни отбора, ни пересортировки — отбор уходит параметрами. */
    async listCompanyPayouts(companyId: string, options?: PayoutsQuery): Promise<PageEnvelope<CompanyPayout>> {
      const path = withQuery(`${companyPath(companyId)}${PAYOUTS_SEGMENT}`, {
        cursor: options?.cursor,
        membership_id: options?.membership_id,
        work_id: options?.work_id,
      });

      return await send<PageEnvelope<CompanyPayout>>(path, 'GET');
    },

    /** Запись выплаты: тело целиком и ключ идемпотентности формы. */
    async createCompanyPayout(
      companyId: string,
      input: CompanyPayoutSubmission,
      idempotencyKey: string,
    ): Promise<CompanyPayout> {
      const path = `${companyPath(companyId)}${PAYOUTS_SEGMENT}`;

      return unwrap(
        await send<DataEnvelope<CompanyPayout>>(path, 'POST', input, {
          [IDEMPOTENCY_HEADER]: idempotencyKey,
        }),
      );
    },

    /** Правка выплаты — `PUT` телом целиком; ключа нет. */
    async updateCompanyPayout(payoutId: string, input: CompanyPayoutSubmission): Promise<CompanyPayout> {
      return unwrap(await send<DataEnvelope<CompanyPayout>>(companyPayoutPath(payoutId), 'PUT', input));
    },

    /** Удаление выплаты — `204` без тела: разворачивать нечего. */
    async deleteCompanyPayout(payoutId: string): Promise<void> {
      await send<unknown>(companyPayoutPath(payoutId), 'DELETE');
    },
  };
}
